/**
 * @file relative-time.ts
 * @description Pure helper that converts an ISO-8601 timestamp into one of
 *   a handful of UI buckets — "today", "yesterday", "n days ago",
 *   "n weeks ago", "n months ago", "n years ago" — plus an "invalid"
 *   sentinel for unparseable input. The actual translated copy lives in
 *   i18n; this helper only returns the bucket + count so the caller can
 *   look up the right plural variant.
 * @module Lib
 * @security No PII, no network. Pure function — easy to test, easy to
 *   inline in list rows without performance concern.
 */

export type RelativeBucket =
  'today' | 'yesterday' | 'days' | 'weeks' | 'months' | 'years' | 'invalid';

export interface RelativeTime {
  bucket: RelativeBucket;
  /** Count is 0 for today/yesterday/invalid; the integer count otherwise. */
  count: number;
}

const MS_PER_DAY = 86_400_000;

/**
 * Bucket an ISO timestamp relative to `now`. Negative deltas (future
 * timestamps) collapse to "today" — last_login_at should never be in
 * the future, but if clock drift produces one we'd rather show
 * "today" than "-1 days ago".
 */
export function relativeTime(iso: string, now: Date = new Date()): RelativeTime {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return { bucket: 'invalid', count: 0 };
  const deltaMs = now.getTime() - then.getTime();
  if (deltaMs < 0) return { bucket: 'today', count: 0 };
  const days = Math.floor(deltaMs / MS_PER_DAY);
  if (days === 0) return { bucket: 'today', count: 0 };
  if (days === 1) return { bucket: 'yesterday', count: 0 };
  if (days < 7) return { bucket: 'days', count: days };
  if (days < 30) return { bucket: 'weeks', count: Math.floor(days / 7) };
  if (days < 365) return { bucket: 'months', count: Math.floor(days / 30) };
  return { bucket: 'years', count: Math.floor(days / 365) };
}
